import { useEffect, useRef, useState } from 'react';
import type * as Monaco from 'monaco-editor';
import { globalBus } from '../streams/wsBus';
import { useActionFeedback } from '../streams/useActionFeedback';

export function FilesPanel() {
  const [filesTree, setFilesTree] = useState<any[]>([]);
  const [activePath, setActivePath] = useState<string>('README.md');
  const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null);
  const editorContainer = useRef<HTMLDivElement | null>(null);
  const { states, fire } = useActionFeedback(globalBus);

  useEffect(() => {
    const unsubscribe = globalBus.subscribe((message) => {
      if (message.t === 'scene' && message.id === 'hivecore.files') {
        const treeWidget = message.body?.widgets?.find((w: any) => w.key === 'files.tree');
        if (treeWidget?.nodes) {
          setFilesTree(treeWidget.nodes);
        }
        const bufferWidget = message.body?.widgets?.find((w: any) => w.key === 'files.buffer');
        const text = bufferWidget?.value ?? '';
        if (editorRef.current && typeof text === 'string' && editorRef.current.getValue() !== text) {
          editorRef.current.setValue(text);
        }
      }
    });
    globalBus.sendIntent('scene.request', { id: 'hivecore.files' });
    globalBus.sendIntent('files.open', { id: activePath });
    return unsubscribe;
  }, [activePath]);

  useEffect(() => {
    (async () => {
      const monacoModule = await import('monaco-editor');
      if (editorContainer.current) {
        const editor = monacoModule.editor.create(editorContainer.current, {
          value: '',
          language: 'markdown',
          theme: 'vs-dark',
          automaticLayout: true,
          minimap: { enabled: false }
        });
        editorRef.current = editor;
      }
    })();
  }, []);

  const openFile = (id: string) => {
    setActivePath(id);
    globalBus.sendIntent('files.open', { id });
  };

  const saveFile = () => {
    const content = editorRef.current?.getValue() ?? '';
    fire(`files.save.${activePath}`, 'files.save', { id: activePath, content });
  };

  return (
    <div className="panel" style={{ display: 'flex', gap: '12px' }}>
      <aside className="files-tree" aria-label="File Explorer">
        <h3 style={{ marginBottom: '8px' }}>Files</h3>
        <ul>
          {filesTree.map((node: any) => (
            <li key={node.id}>
              <button
                className={node.id === activePath ? 'active' : ''}
                onClick={() => openFile(node.id)}
              >
                {node.label ?? node.id}
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <section style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '60vh' }}>
        <header className="panel-header">
          <h2>{activePath}</h2>
          <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
            <button onClick={() => globalBus.sendIntent('files.revert', { id: activePath })}>Revert</button>
            <button data-state={states[`files.save.${activePath}`] ?? 'idle'} onClick={saveFile}>Save</button>
          </div>
        </header>
        <div ref={editorContainer} style={{ flex: 1, border: '1px solid var(--border)', borderRadius: '12px' }} />
      </section>
    </div>
  );
}
